import { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Input from '../../components/ui/Input.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Modal from '../../components/ui/Modal.jsx';
import { FileText, Download, Settings2, Save, Trash2, RotateCcw, AlertTriangle } from 'lucide-react';
import LeaveTemplatePage from './LeaveTemplatePage.jsx';
import { cn, formatDays } from '@/lib/utils';

const STATUS_VARIANT = { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'default' };
const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];
const STATUS_OPTIONS = ['pending', 'approved', 'rejected', 'cancelled'];

export default function LeaveRequestsPage() {
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [showTemplateConfig, setShowTemplateConfig] = useState(false);

  // Admin edit form mirrors `selected` but is locally mutable until Save.
  const [edit, setEdit] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refresh = () => {
    setLoading(true);
    return adminApi.listLeaveRequests()
      .then((res) => setRequests(res.requests || []))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    adminApi.listLeaveTypes()
      .then((res) => setLeaveTypes(res.types || res.leaveTypes || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When a request is selected, seed the edit form. Reset confirm state too.
  useEffect(() => {
    if (selected) {
      setEdit({
        typeId: selected.typeId || '',
        startDate: selected.startDate || '',
        endDate: selected.endDate || '',
        days: selected.days ?? 0,
        reason: selected.reason || '',
        status: selected.status || 'pending',
      });
      setConfirmDelete(false);
    } else {
      setEdit(null);
    }
  }, [selected]);

  const filtered = useMemo(() => {
    let list = requests;
    if (filter !== 'all') list = list.filter((r) => r.status === filter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        r.requesterName?.toLowerCase().includes(s) ||
        r.requesterEmail?.toLowerCase().includes(s) ||
        r.typeName?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [requests, filter, search]);

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  const handleExport = async (r) => {
    try {
      const blob = await adminApi.exportLeaveRequest(r.partitionKey, r.rowKey);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${r.requesterName || 'Leave'}_${r.startDate || r.rowKey}_LeaveRequest.xlsx`.replace(/[^a-zA-Z0-9_.-]/g, '_');
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(`Export failed: ${err.message}`);
    }
  };

  const handleSave = async () => {
    if (!selected || !edit) return;
    setBusy(true);
    try {
      const updates = {};
      if (edit.typeId !== selected.typeId) updates.typeId = edit.typeId;
      if (edit.startDate !== selected.startDate) updates.startDate = edit.startDate;
      if (edit.endDate !== selected.endDate) updates.endDate = edit.endDate;
      const editDaysRounded = Math.round(Number(edit.days) * 100) / 100;
      if (editDaysRounded !== Number(selected.days)) updates.days = editDaysRounded;
      if (edit.reason !== selected.reason) updates.reason = edit.reason;
      if (edit.status !== selected.status) updates.status = edit.status;
      if (Object.keys(updates).length === 0) {
        toast.info?.('No changes to save') || toast.success('No changes to save');
        return;
      }
      await adminApi.updateLeaveRequest(selected.partitionKey, selected.rowKey, updates);
      toast.success('Leave request updated');
      setSelected(null);
      await refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await adminApi.deleteLeaveRequest(selected.partitionKey, selected.rowKey);
      toast.success('Leave request deleted');
      setSelected(null);
      await refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRestart = async (fromStep) => {
    if (!selected) return;
    setBusy(true);
    try {
      await adminApi.restartLeaveChain(selected.partitionKey, selected.rowKey, fromStep);
      toast.success(fromStep === 0 ? 'Approval chain restarted' : `Approval chain restarted from step ${fromStep + 1}`);
      setSelected(null);
      await refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'requesterName', label: 'Employee',
      render: (r) => (
        <div>
          <div className="font-medium">{r.requesterName}</div>
          <div className="text-xs text-muted-foreground">{r.requesterDepartment}</div>
        </div>
      ),
    },
    {
      key: 'typeName', label: 'Type',
      render: (r) => <Badge variant="default">{r.typeName || r.typeId}</Badge>,
    },
    {
      key: 'dates', label: 'Dates',
      render: (r) => <div className="text-xs">{r.startDate} → {r.endDate}</div>,
    },
    {
      key: 'days', label: 'Days',
      render: (r) => <span className="font-mono font-medium">{formatDays(r.days)}</span>,
    },
    {
      key: 'status', label: 'Status',
      render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'default'}>{r.status}</Badge>,
    },
    {
      key: 'submittedAt', label: 'Submitted',
      render: (r) => {
        if (!r.submittedAt) return '—';
        return new Date(r.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      },
    },
    {
      key: 'export', label: '',
      render: (r) => (
        <button
          onClick={(e) => { e.stopPropagation(); handleExport(r); }}
          className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
          title="Export to XLSX"
        >
          <FileText className="h-3.5 w-3.5" />
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Leave Requests</h1>
          <p className="text-muted-foreground">
            {requests.length} total · {pendingCount} pending approval
          </p>
        </div>
        <Button variant="ghost" onClick={() => setShowTemplateConfig(!showTemplateConfig)}>
          <Settings2 className="h-4 w-4" />
          Export Template
        </Button>
      </div>

      {showTemplateConfig && (
        <div className="mb-6 border border-border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between bg-muted/30 px-4 py-2 border-b border-border">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Export Template Configuration</span>
            <button onClick={() => setShowTemplateConfig(false)} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Close</button>
          </div>
          <div className="p-4">
            <LeaveTemplatePage />
          </div>
        </div>
      )}

      <div className="flex gap-3 mb-5 items-end flex-wrap">
        <Input label="Search" placeholder="Name, type..." value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 max-w-[300px]" />
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button key={f.value} onClick={() => setFilter(f.value)} className={cn('rounded-md border px-3 py-1.5 text-sm transition-colors cursor-pointer', filter === f.value ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-input text-muted-foreground hover:bg-muted')}>{f.label}</button>
          ))}
        </div>
      </div>

      <DataTable columns={columns} rows={filtered} loading={loading} emptyMessage="No leave requests." keyFn={(r) => r.rowKey} onRowClick={(r) => setSelected(r)} />

      {selected && edit && (
        <Modal isOpen onClose={() => setSelected(null)} title="Leave Request Detail" width="640px">
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Employee</div>
                <div className="font-medium">{selected.requesterName}</div>
                <div className="text-xs text-muted-foreground">{selected.requesterEmail}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Submitted</div>
                <div>{selected.submittedAt ? new Date(selected.submittedAt).toLocaleString() : '—'}</div>
              </div>
            </div>

            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                Admin Override — edits apply silently and are audited
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[13px] font-medium text-foreground mb-1">Leave Type</label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    value={edit.typeId}
                    onChange={(e) => setEdit({ ...edit, typeId: e.target.value })}
                  >
                    {leaveTypes.length === 0 && <option value={edit.typeId}>{edit.typeId}</option>}
                    {leaveTypes.map((t) => (
                      <option key={t.rowKey} value={t.rowKey}>{t.displayName || t.rowKey}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-foreground mb-1">Status</label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    value={edit.status}
                    onChange={(e) => setEdit({ ...edit, status: e.target.value })}
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Input label="Start Date" type="date" value={edit.startDate} onChange={(e) => setEdit({ ...edit, startDate: e.target.value })} />
                <Input label="End Date" type="date" value={edit.endDate} onChange={(e) => setEdit({ ...edit, endDate: e.target.value })} />
                <Input label="Days" type="number" step="0.01" min="0" value={edit.days} onChange={(e) => setEdit({ ...edit, days: e.target.value })} />
              </div>

              <Input label="Reason" value={edit.reason} onChange={(e) => setEdit({ ...edit, reason: e.target.value })} />

              <div className="flex justify-between gap-2 pt-1">
                {!confirmDelete ? (
                  <Button variant="ghost" onClick={() => setConfirmDelete(true)} disabled={busy} className="text-destructive hover:bg-destructive/10">
                    <Trash2 className="h-4 w-4" /> Delete request
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-destructive font-medium">Confirm permanent delete?</span>
                    <Button variant="danger" onClick={handleDelete} loading={busy}>
                      <Trash2 className="h-4 w-4" /> Yes, delete
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmDelete(false)} disabled={busy}>Cancel</Button>
                  </div>
                )}
                <Button onClick={handleSave} loading={busy} disabled={busy}>
                  <Save className="h-4 w-4" /> Save Changes
                </Button>
              </div>
            </div>

            {selected.chainSnapshot?.length > 0 && (
              <div className="rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Approval Chain</div>
                  <Button variant="secondary" size="sm" onClick={() => handleRestart(0)} disabled={busy}>
                    <RotateCcw className="h-3.5 w-3.5" /> Restart from start
                  </Button>
                </div>
                <div className="space-y-2">
                  {selected.chainSnapshot.map((step, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs rounded-md bg-muted/30 px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-muted-foreground">{i + 1}.</span>
                        <span className="truncate">{step.label || step.email || step.value}</span>
                        {step.status === 'approved' && <Badge variant="success">Approved</Badge>}
                        {step.status === 'rejected' && <Badge variant="danger">Rejected</Badge>}
                        {step.status === 'pending' && <Badge variant="warning">Pending</Badge>}
                      </div>
                      <button
                        onClick={() => handleRestart(i)}
                        disabled={busy}
                        className="flex-shrink-0 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-background hover:border-primary/40 transition-colors cursor-pointer disabled:opacity-50"
                        title={`Restart chain from step ${i + 1}`}
                      >
                        Restart from here
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-border pt-3 flex justify-end">
              <Button variant="secondary" onClick={() => handleExport(selected)}>
                <Download className="h-4 w-4" /> Export XLSX
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
